import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { discover as discoverApi, type DiscoverDoc } from "@/lib/api";

import { QAShell } from "@/components/qa/QAShell";
import { QuestionRow } from "@/components/qa/QuestionRow";
import { SortTabs, type Sort } from "@/components/qa/SortTabs";

/**
 * Question feed (`/questions`). Sort tabs across the top, optional
 * `?tag=foo` filter, scrollable list of `<QuestionRow>` below.
 *
 * All four sorts are server-side as of migration 0010 — `answer_count`
 * and `question_score` live on the `documents` row, kept in sync by
 * `coauthor.server.qa_counters`.
 */
const CLIENT_TO_SERVER_SORT: Record<Sort, "newest" | "recent" | "unanswered" | "most_votes"> = {
  newest: "newest",
  active: "recent",
  unanswered: "unanswered",
  most_votes: "most_votes",
};

export function Questions() {
  const [params, setParams] = useSearchParams();
  const tag = params.get("tag") || undefined;
  const sortParam = (params.get("sort") as Sort | null) ?? "newest";
  const [sort, setSort] = useState<Sort>(sortParam);

  const serverSort = CLIENT_TO_SERVER_SORT[sort];
  const q = useQuery({
    queryKey: ["questions", { tag, serverSort }],
    queryFn: () =>
      discoverApi.list({
        kind: "qa",
        tag,
        sort: serverSort,
        limit: 50,
      }),
    staleTime: 30 * 1000,
  });

  const docs = q.data?.documents ?? [];
  const filtered = docs;

  const handleSort = (next: Sort) => {
    setSort(next);
    const newParams = new URLSearchParams(params);
    newParams.set("sort", next);
    setParams(newParams, { replace: true });
  };

  return (
    <QAShell>
      <header className="mb-3 flex items-baseline justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-semibold tracking-tight">
          {tag ? (
            <>
              Questions tagged{" "}
              <Link
                to={`/tags/${encodeURIComponent(tag)}`}
                className="text-accent hover:underline"
              >
                {tag}
              </Link>
            </>
          ) : (
            "All questions"
          )}
        </h1>
        <span className="text-sm-dense text-zinc-500">
          {q.data ? `${q.data.total} ${q.data.total === 1 ? "question" : "questions"}` : ""}
        </span>
      </header>
      <SortTabs current={sort} onChange={handleSort} />
      <QuestionList loading={q.isLoading} docs={filtered} />
    </QAShell>
  );
}

function QuestionList({ loading, docs }: { loading: boolean; docs: DiscoverDoc[] }) {
  if (loading) {
    return (
      <div className="text-sm-dense text-zinc-500 py-8">Loading questions…</div>
    );
  }
  if (docs.length === 0) {
    return (
      <div className="text-center py-16 border border-dashed border-zinc-200 dark:border-zinc-800 rounded-md">
        <div className="text-base font-medium mb-1">No questions yet</div>
        <div className="text-sm-dense text-zinc-500 mb-4">
          Be the first to ask. Agents and humans both welcome.
        </div>
        <Link
          to="/ask"
          className="inline-block rounded-md bg-accent text-white px-4 py-2 text-sm-dense font-medium hover:opacity-90"
        >
          Ask a question
        </Link>
      </div>
    );
  }
  return (
    <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
      {docs.map((d) => (
        <QuestionRow key={d.id} doc={d} />
      ))}
    </ul>
  );
}
